// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { t } from '@/i18n';

// Visual grouping of games shown on the selection, round-results and tour-results screens.
// Purely organisational: it does not affect selection rules, scoring, weights or recommendations.
export type GameGroupId = 'main' | 'match' | 'team' | 'hazard' | 'achievement' | 'challenge' | 'other';

// Display order of the group headers. `other` is the fallback bucket and always sorts last.
export const GAME_GROUP_ORDER: GameGroupId[] = [
  'main',
  'match',
  'team',
  'hazard',
  'achievement',
  'challenge',
  'other',
];

// Group membership keyed by the game definition's stable GUID (lowercased). GUIDs mirror the
// backend seeder (services/game-definition-service/.../DefaultGameDefinitionSeeder.cs). Worst Ball
// was removed and is intentionally absent.
const GROUP_BY_GAME_ID: Record<string, GameGroupId> = {
  // Main games
  'a4f4b9a1-2db2-46e8-a615-bf53f2fbcb9a': 'main', // Stableford
  'f3c73b75-9d64-4df8-a4fe-7f927cb9fbe1': 'main', // Stroke Play
  // Match games
  '0b2f4ee8-fb3e-4d4b-91d4-64848ec4ea9e': 'match', // Skins
  '8173497d-7cb7-4eca-8906-83cf45e178d3': 'match', // Nassau
  '2f1c8d3a-6b54-4e29-9a7d-1c0e5b8f3a42': 'match', // Köpenhamn
  // Team games
  '0ed4ff89-eec3-4f00-88de-0990a11e0f3e': 'team', // Best Ball
  '9e183962-7a3d-4719-b7e3-f64f095b8b6e': 'team', // Best ball - worst ball
  // Hazard games
  'e0b7c8af-3dc8-4a9f-8b77-5d99e5da0a4b': 'hazard', // Water
  '4d3a1a42-4f67-41a4-b63d-9a9b50a83f1f': 'hazard', // Bunker
  '6b9c72e0-6c1c-4d17-9f1f-6c0e2f71a742': 'hazard', // Penalty
  'f9d88a1b-1a7e-4c86-9b2e-8e7f7f0fcb2b': 'hazard', // 3 putts
  // Achievement games
  '7a2e1c94-3b6d-4f81-9c2a-5d8e0f1a2b3c': 'achievement', // Chip in
  'c20fa4cf-fadc-4407-bd6c-9c190c9966fc': 'achievement', // Birdie or better
  'f58b81d9-e369-4b42-b225-0fc4ce470f95': 'achievement', // Sandy par
  '0e8efec8-4235-466d-b45f-5d4557799ee9': 'achievement', // Wet par
  // Challenges
  'b1f4b6d5-3a7e-4d0a-8d3b-2e4f6a7b8c9d': 'challenge', // Mystery
};

// Normalizes a game name/id to a kebab-case key (also used for icon lookups across screens).
export const normalizeGameKey = (value: string | undefined | null): string => {
  if (!value) return '';
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

// Fallback membership keyed by normalized name, for score rows that carry a game name but no id.
const GROUP_BY_GAME_KEY: Record<string, GameGroupId> = {
  stableford: 'main',
  'stroke-play': 'main',
  skins: 'match',
  nassau: 'match',
  'kopenhamn': 'match',
  'kopenhamn-copenhagen': 'match',
  'best-ball': 'team',
  'best-ball-worst-ball': 'team',
  water: 'hazard',
  bunker: 'hazard',
  penalty: 'hazard',
  '3-putts': 'hazard',
  'chip-in': 'achievement',
  'birdie-or-better': 'achievement',
  'sandy-par': 'achievement',
  'wet-par': 'achievement',
  mystery: 'challenge',
};

// Resolves a game's group: by GUID first, then by normalized name, else the `other` fallback.
export const getGameGroupId = (gameId?: string | null, gameName?: string | null): GameGroupId => {
  const id = (gameId ?? '').trim().toLowerCase();
  if (id && GROUP_BY_GAME_ID[id]) {
    return GROUP_BY_GAME_ID[id];
  }

  const key = normalizeGameKey(gameName);
  if (key && GROUP_BY_GAME_KEY[key]) {
    return GROUP_BY_GAME_KEY[key];
  }

  return 'other';
};

export const getGameGroupTitle = (groupId: GameGroupId): string => t(`gameGroups.${groupId}`);

export type GameGroup<T> = {
  groupId: GameGroupId;
  title: string;
  items: T[];
};

// Buckets items into GAME_GROUP_ORDER, preserving incoming order within each bucket and omitting
// empty groups. Drives the grouped rendering on all three screens.
export function groupItems<T>(
  items: T[],
  getId: (item: T) => string | undefined | null,
  getName: (item: T) => string | undefined | null,
): GameGroup<T>[] {
  const buckets = new Map<GameGroupId, T[]>();
  items.forEach((item) => {
    const groupId = getGameGroupId(getId(item), getName(item));
    const bucket = buckets.get(groupId) ?? [];
    bucket.push(item);
    buckets.set(groupId, bucket);
  });

  return GAME_GROUP_ORDER.flatMap((groupId) => {
    const bucket = buckets.get(groupId);
    if (!bucket || bucket.length === 0) {
      return [];
    }

    return [{ groupId, title: getGameGroupTitle(groupId), items: bucket }];
  });
}
